<template>
  <modal
    v-if="isShow"
    width="700px"
    maxHeight="901px"
    height="100%"
    bodyPadding="0"
    :title="activeWorkspace.name"
    @close="close"
    :read-article-url="`https://docs.bemob.com/${locale}/workspaces`"
  >
    <div class="tabs mt-16">
      <div class="tabs-head">
        <div
          v-for="tabKey in tabs"
          :key="tabKey"
          @click="tab = tabKey"
          :class="{
            'tab-head-item': true,
            'tab-head-item--active': tab === tabKey,
          }"
        >
          <ui-icon size="12" class="mr-4" :icon="`fas ${tabsData[tabKey].icon}`"></ui-icon>
          {{ tabsData[tabKey].title }}
        </div>
      </div>
      <div class="tab-body">
        <div
          v-for="tabKey in tabs"
          :key="tabKey"
          :class="{
            'tab-body-item': true,
            'tab-body-item--active': tab === tabKey,
          }"
        >
          <div class="tab-body-title-block">
            <div class="tab-body-title">
              <ui-icon size="16" class="mr-4" :icon="`fas ${tabsData[tabKey].icon}`"></ui-icon>
              <span class="tab-body-title-text">
                {{ tabsData[tabKey].title }}:
                {{ filteredRowsByTab[tabKey].length }}
              </span>
            </div>
          </div>
          <ui-input
            v-model="search"
            class="tab-body-search"
            :placeholder="t('Search')"
            prepend-inner-icon="fas fa-search"
          ></ui-input>
          <div class="no-data-in-table" v-if="filteredRowsByTab[tabKey].length === 0">
            <img width="70" src="/images/empty-table.png" />
            <div class="no-data-in-table__title">
              {{ t('No Data Found') }}
            </div>
          </div>
          <RecycleScroller
            :items="filteredRowsByTab[tabKey]"
            class="scroller"
            key-field="id"
            :item-size="34"
            v-slot="{ item }"
          >
            <div class="table-row">
              {{ item.name }}
            </div>
          </RecycleScroller>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="justify-end xs12" style="display: flex">
        <ui-button :text="t('Close')" icon="fas fa-times" @click="close"></ui-button>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { getUserWorkspaceItemsApiRequest } from '@/services/api/workspaces-api';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import { useEditAccessPermissions } from './useEditAccessPermissions';

const props = defineProps<{
  data?: {
    activeWorkspace?: Record<string, unknown>;
  };
}>();

const emit = defineEmits<{ close: [] }>();

const { t, locale } = useI18n();
const store = useStore();

const { tabs, tab, tabsData, filteredRowsByTab, search, rows, activeWorkspace, updateTabStatus } =
  useEditAccessPermissions();

const isShow = ref(false);

const close = () => {
  emit('close');
};

onMounted(() => {
  activeWorkspace.value = props.data?.activeWorkspace ?? { id: '', name: '' };
  store.setLoading(true);

  getUserWorkspaceItemsApiRequest({ workspaceId: activeWorkspace.value.id })
    .then((data) => {
      rows.value = data as Record<string, unknown[]>;
      tabs.forEach((tabKey) => {
        if (rows.value[tabKey]) {
          filteredRowsByTab.value[tabKey] = rows.value[tabKey];
        }
      });
      updateTabStatus(tab.value);
      isShow.value = true;
    })
    .finally(() => {
      store.setLoading(false);
    });
});
</script>

<style lang="scss">
.items-in-workspace-dialog.v-dialog.v-dialog--active > .v-card > .v-card__text {
  padding: 0 !important;
}
</style>
<style lang="scss" scoped>

@import '@/styles/dialogs/permissions-dialog';
:deep(.table) {
  .v-table__overflow {
    overflow: auto;
    height: 100%;
  }
}

.tab-body {
  height: calc(100% - 35px);
}
.tabs {
  height: calc(100% - 20px);
}
.scroller {
  height: 100%;
}
</style>
